import * as React from "react";
import classNames from "classnames";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	Chip,
	Button,
	TablePagination,
	ExpansionPanel,
	ExpansionPanelActions,
	ExpansionPanelSummary,
	ExpansionPanelDetails,
	Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DoneIcon from "@material-ui/icons/Done";
import ClearIcon from "@material-ui/icons/Clear";
import { connect } from "react-redux";
import { Payment, getPayments } from '../../api/payments';
import { format, isAfter, differenceInHours } from 'date-fns';
import { StoreState } from '../../reducers/root-reducer';
import { Dispatch } from 'redux';
import { AuthInfo } from "../../api/token-storage";
import { updatePayments, openAddPaymentDialog } from '../../actions/store/app-actions';
import { getPaymentsAndUpdateStore } from '../../actions/payment-actions';
import {
	getReadablePaymentCurrency,
	getReadablePaymentStatus,
	getPaymentCardLabel,
	getChargeDeadline,
	isGuaranteePayment,
	isTimeSet
} from "../../util/general-utility";
import { conf } from "../../conf";
import { ConnectedChargeNoShowDialog } from "./charge-no-show-dialog";

interface PaymentTableProps extends WithStyles<typeof styles> {
	layout: "desktop" | "tablet" | "phone";
	authInfo: AuthInfo;
}

type Props = PaymentTableProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps>;

interface State {
	chargePayment: Payment;
}

export class PaymentList extends React.Component<Props, State> {
	public state: State = {
		chargePayment: null
	};

	public async componentDidMount() {
		getPaymentsAndUpdateStore(1);
	}

	private handlePageChange = (_: any, nextPage: number) => {
		getPaymentsAndUpdateStore(nextPage + 1);
	}

	private handleResendClick = (payment: Payment) => {
		const reservationId = payment.inquiryId ? undefined : payment.reservationId;
		this.props.openAddPaymentDialog(payment.inquiryId, payment.email, payment.name, payment.amount.toFixed(2), 0, payment.message, payment.id, reservationId);
	}

	private handleCopyLinkClick = (payment: Payment) => {
		const el = document.createElement('input');
		el.value = 'https://xandlstadl.guest.tareso.at/payments/' + payment.id;
		el.style.position = 'absolute';
		el.style.zIndex = '-9999';

		document.body.appendChild(el);

		el.select();
		document.execCommand('copy');
		document.body.removeChild(el);
	}

	private handleChargeClick = (payment: Payment) => {
		this.setState({ chargePayment: payment });
	}

	private handleChargeDialogClose = () => {
		this.setState({ chargePayment: null });
	}

	private canBeCharged = (payment: Payment) => {
		return isGuaranteePayment(payment)
			&& payment.state === "completed"
			&& (payment.mandateStatus === "valid" || payment.mandateStatus === "pending")
			&& payment.chargeState !== "paid";
	}

	private renderGuaranteeAttributes = (p: Payment) => {
		const { classes } = this.props;

		if (!isGuaranteePayment(p)) return null;

		const card = getPaymentCardLabel(p);
		const deadline = getChargeDeadline(p.reservationDate, conf.payments.noShowChargeDays);

		return (
			<>
				<Typography className={classes.attributeLabel}>Vereinbarte No-Show-Gebühr</Typography>
				<Typography>{getReadablePaymentCurrency(p.currency, p.amount)} (Höchstbetrag)</Typography>
				{p.chargeState === "paid" && (
					<>
						<Typography className={classes.attributeLabel}>Abgebucht</Typography>
						<Typography>
							{getReadablePaymentCurrency(p.currency, p.chargeAmount)}
							{isTimeSet(p.chargeTime) ? " am " + format(p.chargeTime, "DD.MM.YYYY HH:mm") : ""}
						</Typography>
					</>
				)}
				{card && (
					<>
						<Typography className={classes.attributeLabel}>Kreditkarte</Typography>
						<Typography>{card}{p.cardExpiryDate ? " (gültig bis " + format(p.cardExpiryDate, "MM/YYYY") + ")" : ""}</Typography>
					</>
				)}
				{isTimeSet(p.reservationDate) && (
					<>
						<Typography className={classes.attributeLabel}>Reservierungsdatum</Typography>
						<Typography>{format(p.reservationDate, "DD.MM.YYYY")}</Typography>
					</>
				)}
				{deadline && this.canBeCharged(p) && (
					<>
						<Typography className={classes.attributeLabel}>Abbuchen möglich bis</Typography>
						<Typography>{format(deadline, "DD.MM.YYYY")}</Typography>
					</>
				)}
				{isTimeSet(p.consentTime) && (
					<>
						<Typography className={classes.attributeLabel}>Bedingungen akzeptiert</Typography>
						<Typography>{format(p.consentTime, "DD.MM.YYYY HH:mm")}</Typography>
					</>
				)}
				{p.chargeDescription && (
					<>
						<Typography className={classes.attributeLabel}>Text auf Abrechnung</Typography>
						<Typography>{p.chargeDescription}</Typography>
					</>
				)}
				{p.chargeRequestId && (
					<>
						<Typography className={classes.attributeLabel}>Abbuchung ID</Typography>
						<Typography className={classes.selectableText}>{p.chargeRequestId}</Typography>
					</>
				)}
			</>
		);
	}

	// Amount column: for card guarantees the money actually charged (or the
	// current state), never the agreed fee, which is only the maximum
	private getSummaryAmount = (payment: Payment) => {
		if (!isGuaranteePayment(payment)) {
			return getReadablePaymentCurrency(payment.currency, payment.amount);
		}

		if (payment.chargeState === "paid") {
			return getReadablePaymentCurrency(payment.currency, payment.chargeAmount) + " abgebucht";
		}

		return getReadablePaymentStatus(payment, true);
	}

	private getStateName = (payment: Payment) => {
		if (isGuaranteePayment(payment)) {
			return getReadablePaymentStatus(payment);
		}

		switch (payment.state) {
			case "awaiting_payment":
				return "Erwarte Zahlungseingang";

			case "expired":
				return "Abgelaufen am " + format(payment.expirationDate, "DD.MM.YYYY HH:mm");

			case "pending":
				return "Zahlung wird im Moment verarbeitet";

			case "completed":
				return "Erfolgreich";
		}
	}

	private renderLog = (payment: Payment) => {
		const { classes } = this.props;

		if (!payment.log || payment.log.length === 0) return;

		return (
			<div className={classes.log}>
				{payment.log.reverse().map(log => (
					<React.Fragment key={log.id}>
						<div>
							<Typography className={classes.mono}>{format(log.createdAt, "MMM DD HH:mm:ss")}</Typography>
						</div>
						<div>
							<Typography className={classNames(classes.logType, classes.mono, {
								[classes.logTypeWarning]: log.type === "warning",
								[classes.logTypeError]: log.type === "error",
							})}>
								{log.type}
							</Typography>
						</div>
						<div>
							<Typography className={classes.mono}>{log.message}</Typography>
						</div>
					</React.Fragment>
				))}
			</div>
		);
	}

	public render() {
		const { classes, payments, page, count, authInfo, layout } = this.props;
		const { chargePayment } = this.state;

		return (
			<React.Fragment>
				{payments.map(p => (
					<ExpansionPanel
						key={p.id}
						className={classNames(classes.panel, {
							[classes.panelWaiting]: p.state === "awaiting_payment",
							[classes.panelExpired]: p.state === "expired",
							[classes.panelPending]: p.state === "pending",
							[classes.panelSuccess]: p.state === "completed",
							[classes.panelCharged]: p.chargeState === "paid",
						})}
					>
						<ExpansionPanelSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<div className={classes.expansionSummary}>
								<Typography className={classes.summaryAmount}>{this.getSummaryAmount(p)}</Typography>
								<Typography className={classes.summaryMail}>{p.email}</Typography>
								<Typography className={classes.summaryName}>{p.name}</Typography>
								<Typography className={classes.summaryDate}>{format(p.creationDate, "DD.MM.YYYY")}</Typography>
							</div>
						</ExpansionPanelSummary>
						<ExpansionPanelDetails className={classes.expansionDetails}>
							<div className={classes.attributes}>
								{layout === "phone" && (
									<>
										<Typography className={classes.attributeLabel}>Name</Typography>
										<Typography className={classes.selectableText}>{p.name}<br/>{p.email}</Typography>
									</>
								)}
								<Typography className={classes.attributeLabel}>{isGuaranteePayment(p) ? "Mollie ID" : "Zahlungs ID"}</Typography>
								<Typography className={classes.selectableText}>{p.requestId}</Typography>
								<Typography className={classes.attributeLabel}>Beschreibung</Typography>
								<Typography>{p.transactionName}</Typography>
								<Typography className={classes.attributeLabel}>Status</Typography>
								<Typography>{this.getStateName(p)}</Typography>
								<Typography className={classes.attributeLabel}>Typ</Typography>
								<Typography>
									{isGuaranteePayment(p)
										? (p.inquiryId ? "Kartenhinterlegung (Anfrage)" : "Kartenhinterlegung (allgemein)")
										: (p.inquiryId ? "Anfrage" : "Allgemeine Zahlung")}
								</Typography>
								{this.renderGuaranteeAttributes(p)}
								{p.methodName && (
									<>
										<Typography className={classes.attributeLabel}>Zahlungsart</Typography>
										<Typography>{p.methodName}</Typography>
									</>
								)}
								{p.transactionId && (
									<>
										<Typography className={classes.attributeLabel}>Transaktions-ID</Typography>
										<Typography>{p.transactionId}</Typography>
									</>
								)}
								{p.state === "awaiting_payment" && (
									<>
										<Typography className={classes.attributeLabel}>Gültig bis</Typography>
										<Typography>{format(p.expirationDate, "DD.MM.YYYY HH:mm")}</Typography>
									</>
								)}
								{isTimeSet(p.completionTime) && (
									<>
										<Typography className={classes.attributeLabel}>Abgeschlossen am</Typography>
										<Typography>{format(p.completionTime, "DD.MM.YYYY HH:mm")}</Typography>
									</>
								)}
								{authInfo.isAdmin && p.reservationId && (
									<>
										<Typography className={classes.attributeLabel}><small>Tareso Reservierung-ID</small></Typography>
										<Typography className={classes.selectableText}><small>{p.reservationId}</small></Typography>
									</>
								)}
								{authInfo.isAdmin && p.inquiryId && (
									<>
										<Typography className={classes.attributeLabel}><small>Tareso Anfrage-ID</small></Typography>
										<Typography className={classes.selectableText}><small>{p.inquiryId}</small></Typography>
									</>
								)}
							</div>
							{this.renderLog(p)}
						</ExpansionPanelDetails>
						<ExpansionPanelActions>
							{p.state === "awaiting_payment" && this.props.capabilities.platform !== "ios" && (
									<Button
										size="small"
										color="secondary"
										onClick={() => this.handleCopyLinkClick(p)}
									>
										{isGuaranteePayment(p) ? "Link kopieren" : "Zahlungslink kopieren"}
									</Button>
							)}
							{this.canBeCharged(p) && (
									<Button
										size="small"
										color="secondary"
										className="charge-no-show-button"
										onClick={() => this.handleChargeClick(p)}
									>
										No-Show-Gebühr abbuchen
									</Button>
							)}
							{p.state !== "completed" && (
									<Button
										size="small"
										color="primary"
										onClick={() => this.handleResendClick(p)}
									>
										Erneut senden
									</Button>
							)}
						</ExpansionPanelActions>
					</ExpansionPanel>
				))}
				<TablePagination
					component="div"
					count={count}
					rowsPerPage={10}
					rowsPerPageOptions={[10]}
					page={page - 1}
					onChangePage={this.handlePageChange}
					labelDisplayedRows={(paginationInfo) => <span>Zeige {paginationInfo.from}-{paginationInfo.to} von {paginationInfo.count}</span>}
				/>
				<ConnectedChargeNoShowDialog
					open={Boolean(chargePayment)}
					payment={chargePayment}
					onClose={this.handleChargeDialogClose}
				/>
			</React.Fragment>
		);
	}
}

const styles = (theme: Theme) =>
	createStyles({
		attribute: {
			display: "flex"
		},
		attributes: {
			display: "grid",
			gridTemplateColumns: "auto 1fr",
			gridGap: "2px 20px",
			background: '#FAFAFA',
			padding: 24,
			"@media (max-width: 600px)": {
				marginTop: "-10px",
				gridTemplateColumns: "1fr",
			}
		},
		attributeLabel: {
			fontWeight: 500,
			"@media (max-width: 600px)": {
				marginTop: "10px",
			}
		},
		selectableText: {
			userSelect: "text"
		},
		summaryAmount: {
			minWidth: 200,
			fontWeight: 500,
			"@media (max-width: 767px)": {
				width: "50%",
				order: 1
			}
		},
		summaryDate: {
			marginLeft: "auto",
			"@media (max-width: 767px)": {
				width: "50%",
				textAlign: "right",
				order: 2
			}
		},
		summaryMail: {
			minWidth: 350,
			"@media (max-width: 767px)": {
				minWidth: "auto",
				textAlign: "right",
				order: 4,
				display: "none"
			}
		},
		summaryName: {
			"@media (max-width: 767px)": {
				width: "100%",
				order: 3,
			}
		},
		panel: {
			borderLeft: "4px solid black"
		},
		panelWaiting: {
			borderColor: theme.status.payment.awaitingPayment
		},
		panelSuccess: {
			borderColor: theme.status.payment.completed
		},
		panelCharged: {
			borderColor: theme.status.payment.charged
		},
		panelExpired: {
			borderColor: theme.status.payment.expired
		},
		panelPending: {
			borderColor: theme.status.payment.pending
		},
		panelFailed: {
			borderColor: "#db2828"
		},
		expansionSummary: {
			width: "100%",
			display: "flex",
			alignItems: "center",

			"@media (max-width: 767px)": {
				flexWrap: "wrap",
			}
		},
		expansionDetails: {
			padding: 0,
			display: 'block'
		},
		log: {
			padding: 24,
			background: '#F3F3F3',
			display: 'grid',
			gridTemplateColumns: 'max-content max-content 1fr',
			gridGap: '1px 25px',
			overflowX: 'auto'
		},
		logType: {
			textTransform: 'uppercase'
		},
		logTypeError: {
			color: '#e80c0c'
		},
		logTypeWarning: {
			color: '#ffa500'
		},
		mono: {
			fontFamily: '"Roboto Mono", monospace',
			fontSize: 14
		}
	});

const mapStateToProps = (state: StoreState) => ({
	layout: state.layout,
	capabilities: state.capabilities,
	payments: state.payments,
	authInfo: state.authInfo,
	page: state.paymentPage,
	count: state.paymentCount
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	openAddPaymentDialog: (inquiryId: string, email: string, name: string, amount: string, validHours: number, message: string, paymentId: string, reservationId?: string) => dispatch(openAddPaymentDialog(inquiryId, email, name, amount, validHours, message, paymentId, reservationId))
})

export const ConnectedPaymentList = withStyles(styles)(
	connect(mapStateToProps, mapDispatchToProps)(PaymentList)
);
